import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Slider,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Movie as MovieIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  AccountTree as StructureIcon,
  CameraRoll as CameraRollIcon,
  Engineering as EngineeringIcon,
  Send as SendIcon,
  ContentCopy as ContentCopyIcon,
  VideoCameraBack as VideoCameraBackIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import {
  VideoPromptSession,
  VideoAgentType,
  VideoPromptIteration,
  VideoGenerationParams
} from '../models/videoPrompt';
import { VideoPromptService } from '../services/videoPromptService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`video-tabpanel-${index}`}
      aria-labelledby={`video-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const VideoPromptIterator = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch persisted sessions and assets from backend
  const {
    sessions: backendSessions,
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'VideoPromptSession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true,
  });

  const [tabValue, setTabValue] = useState(0);
  const [sessions, setSessions] = useState<VideoPromptSession[]>([]);
  const [currentSession, setCurrentSession] = useState<VideoPromptSession | null>(null);
  const [newSessionDialog, setNewSessionDialog] = useState(false);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [newSessionPrompt, setNewSessionPrompt] = useState('');
  const [selectedAgentType, setSelectedAgentType] = useState<VideoAgentType>('Expander');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [temperature, setTemperature] = useState(0.7);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [videoParams, setVideoParams] = useState<VideoGenerationParams>({
    duration: 5,
    aspectRatio: '16:9',
    style: 'default',
    quality: 'standard',
    fps: 30
  });

  // Load and merge sessions from localStorage and backend
  useEffect(() => {
    const loadSessions = () => {
      // Load from localStorage
      const localSessions = VideoPromptService.loadSessions();

      // Merge with backend sessions
      if (backendSessions && backendSessions.length > 0) {
        // Convert backend sessions to VideoPromptSession format
        const convertedBackendSessions: VideoPromptSession[] = backendSessions.map(bs => {
          // Parse sessionData JSON to get InitialPrompt, CurrentPrompt, Iterations
          let sessionData: any = {};
          try {
            sessionData = bs.session.sessionData ? JSON.parse(bs.session.sessionData) : {};
          } catch (error) {
            console.error('[VideoPromptIterator] Failed to parse sessionData:', error);
          }

          const initialPrompt = sessionData.InitialPrompt || bs.session.metadata?.initialPrompt || '';
          const currentPrompt = sessionData.CurrentPrompt || bs.session.metadata?.currentPrompt || initialPrompt;
          const iterations = sessionData.Iterations || [];

          // Generate title from initial prompt if not provided
          // Use first 10 words with ellipses if longer
          const generateTitle = (prompt: string): string => {
            if (!prompt) return 'Untitled Video Session';
            const words = prompt.trim().split(/\s+/);
            if (words.length <= 10) {
              return words.join(' ');
            }
            return words.slice(0, 10).join(' ') + '...';
          };

          const title = bs.session.title || bs.session.metadata?.title || generateTitle(initialPrompt);

          return {
            id: bs.session.id,
            title: title,
            initialPrompt: initialPrompt,
            currentPrompt: currentPrompt,
            sessionType: (bs.session.sessionType as 'Standard' | 'Niche') || 'Standard',
            iterations: iterations,
            createdAt: new Date(bs.session.createdAt),
            lastUpdatedAt: bs.session.updatedAt ? new Date(bs.session.updatedAt) : new Date(bs.session.createdAt),
          };
        });

        // Merge: prefer backend sessions (they're authoritative)
        const sessionMap = new Map<string, VideoPromptSession>();

        // Add local sessions first
        localSessions.forEach(s => sessionMap.set(s.id, s));

        // Override with backend sessions
        convertedBackendSessions.forEach(s => sessionMap.set(s.id, s));

        setSessions(Array.from(sessionMap.values()));
      } else {
        setSessions(localSessions);
      }
    };

    loadSessions();
  }, [backendSessions]);

  const createNewSession = async () => {
    if (!newSessionTitle || !newSessionPrompt) return;

    setLoading(true);
    setError(null);

    try {
      // Call the API to start a new session
      const response = await VideoPromptService.startSession(newSessionPrompt);
      
      // Create a local session object from the response
      const session: VideoPromptSession = {
        id: response.sessionId,
        title: newSessionTitle,
        initialPrompt: newSessionPrompt,
        currentPrompt: response.initialPrompt || newSessionPrompt,
        sessionType: 'Standard',
        createdAt: new Date(),
        lastUpdatedAt: new Date(),
        iterations: [],
      };

      // Save to local storage and set as current
      VideoPromptService.saveSession(session);
      setCurrentSession(session);
      setNewSessionDialog(false);
      setNewSessionTitle('');
      setNewSessionPrompt('');
      refetchBackendData();
      
    } catch (err) {
      setError('Failed to create session. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectSession = (session: VideoPromptSession) => {
    setCurrentSession(session);
  };

  const deleteSession = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    VideoPromptService.deleteSession(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
    refetchBackendData();
  };

  const generateIteration = async () => {
    if (!currentSession) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Add user action if there are additional instructions
      if (additionalInstructions.trim()) {
        const userAction: VideoPromptIteration = {
          iterationNumber: currentSession.iterations.length,
          prompt: currentSession.currentPrompt,
          agentType: selectedAgentType,
          userInstruction: additionalInstructions,
          isUserAction: true,
          timestamp: new Date(),
        };
        
        currentSession.iterations.push(userAction);
      }

      const response = await VideoPromptService.generateIteration(currentSession.id, {
        agentType: selectedAgentType,
        additionalInstructions,
        temperature,
        videoParams: showAdvancedOptions ? videoParams : undefined
      });

      // Handle both API response formats
      const improvedPrompt = (response as any).improvedPrompt || response.processedPrompt;
      const previousPrompt = (response as any).previousPrompt || response.originalPrompt;
      
      const newIteration: VideoPromptIteration = {
        iterationNumber: response.iterationNumber,
        prompt: improvedPrompt,
        promptVersion: previousPrompt,
        agentType: selectedAgentType,
        timestamp: new Date(),
        generatedVideoUrl: response.videoGenerationUrl,
        thumbnailUrl: response.thumbnailUrl,
        duration: response.metadata?.duration,
        aspectRatio: videoParams.aspectRatio,
        style: videoParams.style,
      };

      currentSession.iterations.push(newIteration);
      currentSession.currentPrompt = improvedPrompt;
      
      VideoPromptService.updateSession(currentSession);
      setCurrentSession({ ...currentSession });
      setAdditionalInstructions('');
    } catch (err) {
      setError('Failed to generate iteration. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateVideo = async () => {
    if (!currentSession) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await VideoPromptService.generateVideo({
        prompt: currentSession.currentPrompt,
        duration: videoParams.duration,
        aspectRatio: videoParams.aspectRatio,
        style: videoParams.style
      });

      // Update the last iteration with video information
      const lastIteration = currentSession.iterations[currentSession.iterations.length - 1];
      if (lastIteration) {
        lastIteration.generatedVideoUrl = response.videoUrl;
        lastIteration.thumbnailUrl = response.thumbnailUrl;
        VideoPromptService.updateSession(currentSession);
        setCurrentSession({ ...currentSession });
      }
    } catch (err) {
      setError('Failed to generate video. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const exportSession = () => {
    if (currentSession) {
      VideoPromptService.exportSession(currentSession);
    }
  };

  const getAgentIcon = (agentType: VideoAgentType) => {
    switch (agentType) {
      case 'Expander':
        return <ExpandMoreIcon />;
      case 'Structurer':
        return <StructureIcon />;
      case 'Cinematic':
        return <CameraRollIcon />;
      case 'Technical':
        return <EngineeringIcon />;
      default:
        return <VideoCameraBackIcon />;
    }
  };

  const getAgentColor = (agentType: VideoAgentType) => {
    switch (agentType) {
      case 'Expander':
        return 'primary';
      case 'Structurer':
        return 'secondary';
      case 'Cinematic':
        return 'success';
      case 'Technical':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container 
      maxWidth="lg"
      sx={{ 
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Header */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: { xs: 2, sm: 3, md: 4 },
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
          }}
        >
          <MovieIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          Video Prompt Iterator
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.9rem', sm: '1rem' },
            px: { xs: 1, sm: 0 },
          }}
        >
          Iteratively refine your video prompts with AI-powered agents
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }} elevation={0}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: { xs: 100, sm: 120 },
            }
          }}
        >
          <Tab label="Sessions" />
          <Tab label="Create New" />
          {currentSession && <Tab label="Current Session" />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {/* Sessions List */}
        {sessions.length === 0 ? (
          <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
              No sessions found
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => setTabValue(1)}
              size={isMobile ? 'medium' : 'large'}
              fullWidth={isMobile}
              sx={{ mt: { xs: 1, sm: 2 } }}
            >
              Create Your First Session
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
              {sessions.map((session) => (
                <Card 
                  key={session.id} 
                  variant="outlined" 
                  sx={{ cursor: 'pointer' }} 
                  onClick={() => { selectSession(session); setTabValue(2); }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between', 
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 0 },
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h6"
                          sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
                        >
                          {session.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                        >
                          Created: {new Date(session.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={(e) => deleteSession(session.id, e)}
                        color="error"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Create New Session */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <TextField
              fullWidth
              label="Session Title"
              value={newSessionTitle}
              onChange={(e) => setNewSessionTitle(e.target.value)}
              size={isMobile ? 'small' : 'medium'}
            />
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              label="Initial Prompt"
              value={newSessionPrompt}
              onChange={(e) => setNewSessionPrompt(e.target.value)}
              size={isMobile ? 'small' : 'medium'}
            />
            <Button
              fullWidth
              size={isMobile ? 'medium' : 'large'}
              variant="contained"
              onClick={createNewSession}
              disabled={!newSessionTitle || !newSessionPrompt || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
            >
              {loading ? 'Creating...' : 'Create Session'}
            </Button>
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}
          </Box>
        </Paper>
      </TabPanel>

      {currentSession && (
        <TabPanel value={tabValue} index={2}>
          {/* Current Session */}
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              {/* Session Header */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                mb: { xs: 1, sm: 2 },
                gap: { xs: 1, sm: 0 },
              }}>
                <Typography 
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  {currentSession.title}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 1 },
                  width: { xs: '100%', sm: 'auto' },
                }}>
                  <Button
                    startIcon={!isMobile && <DownloadIcon />}
                    onClick={exportSession}
                    size={isMobile ? 'small' : 'medium'}
                    fullWidth={isMobile}
                  >
                    Export
                  </Button>
                  {/* Generate Video button hidden per user request */}
                  {/* <Button
                    startIcon={<VideoCameraBackIcon />}
                    variant="contained"
                    color="success"
                    onClick={generateVideo}
                    disabled={loading || !currentSession.currentPrompt}
                  >
                    Generate Video
                  </Button> */}
                </Box>
              </Box>

              {/* Current Prompt */}
              <Box>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  }}
                >
                  Current Prompt
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 3}
                  value={currentSession.currentPrompt}
                  size={isMobile ? 'small' : 'medium'}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton 
                        size="small" 
                        onClick={() => navigator.clipboard.writeText(currentSession.currentPrompt)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    )
                  }}
                />
              </Box>

              {/* Controls */}
              <Box>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.125rem', sm: '1.25rem' },
                  }}
                >
                  Generate Iteration
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Agent Type</InputLabel>
                    <Select
                      value={selectedAgentType}
                      onChange={(e) => setSelectedAgentType(e.target.value as VideoAgentType)}
                      label="Agent Type"
                    >
                      <MenuItem value="Expander">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ExpandMoreIcon />
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Expander
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Structurer">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StructureIcon />
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Structurer
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Cinematic">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CameraRollIcon />
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Cinematic
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="Technical">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EngineeringIcon />
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Technical
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Box>
                    <Typography 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Temperature: {temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, value) => setTemperature(value as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      marks
                      sx={{
                        '& .MuiSlider-thumb': {
                          width: { xs: 18, sm: 20 },
                          height: { xs: 18, sm: 20 },
                        },
                      }}
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Additional Instructions"
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    size={isMobile ? 'small' : 'medium'}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        generateIteration();
                      }
                    }}
                  />

                  <Button
                    fullWidth
                    size={isMobile ? 'medium' : 'large'}
                    variant="contained"
                    onClick={generateIteration}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  >
                    {loading ? 'Generating...' : 'Generate Iteration'}
                  </Button>
                </Box>
              </Box>

              {/* Advanced Options */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAdvancedOptions}
                      onChange={(e) => setShowAdvancedOptions(e.target.checked)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Show advanced options
                    </Typography>
                  }
                />
                {showAdvancedOptions && (
                  <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Duration (seconds)"
                      value={videoParams.duration}
                      onChange={(e) => setVideoParams({ ...videoParams, duration: parseInt(e.target.value) })}
                      size={isMobile ? 'small' : 'medium'}
                    />
                    <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                      <InputLabel>Aspect Ratio</InputLabel>
                      <Select
                        value={videoParams.aspectRatio}
                        onChange={(e) => setVideoParams({ ...videoParams, aspectRatio: e.target.value })}
                        label="Aspect Ratio"
                      >
                        <MenuItem value="16:9">16:9</MenuItem>
                        <MenuItem value="4:3">4:3</MenuItem>
                        <MenuItem value="1:1">1:1</MenuItem>
                        <MenuItem value="9:16">9:16</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                      <InputLabel>Style</InputLabel>
                      <Select
                        value={videoParams.style}
                        onChange={(e) => setVideoParams({ ...videoParams, style: e.target.value })}
                        label="Style"
                      >
                        <MenuItem value="default">Default</MenuItem>
                        <MenuItem value="cinematic">Cinematic</MenuItem>
                        <MenuItem value="animated">Animated</MenuItem>
                        <MenuItem value="documentary">Documentary</MenuItem>
                        <MenuItem value="experimental">Experimental</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Quality</InputLabel>
                      <Select
                        value={videoParams.quality}
                        onChange={(e) => setVideoParams({ ...videoParams, quality: e.target.value as any })}
                        label="Quality"
                      >
                        <MenuItem value="standard">Standard</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="premium">Premium</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert severity="error">
                  {error}
                </Alert>
              )}

              {/* Iterations */}
              {currentSession.iterations.length > 0 && (
                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: '1.125rem', sm: '1.25rem' },
                    }}
                  >
                    Iteration History
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                    {currentSession.iterations.map((iteration, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'center' }, 
                            mb: { xs: 1, sm: 1 },
                            gap: { xs: 1, sm: 0 },
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              flexWrap: 'wrap',
                              gap: { xs: 0.75, sm: 1 },
                            }}>
                              <Avatar sx={{ 
                                bgcolor: 'primary.main', 
                                width: { xs: 20, sm: 24 }, 
                                height: { xs: 20, sm: 24 },
                              }}>
                                {iteration.isUserAction ? 
                                  <PersonIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> : 
                                  <BotIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                }
                              </Avatar>
                              <Chip
                                icon={getAgentIcon(iteration.agentType)}
                                label={iteration.agentType}
                                color={getAgentColor(iteration.agentType)}
                                size="small"
                                sx={{
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  height: { xs: 20, sm: 24 },
                                }}
                              />
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  display: { xs: 'block', sm: 'inline' },
                                  width: { xs: '100%', sm: 'auto' },
                                }}
                              >
                                Iteration {iteration.iterationNumber} • {new Date(iteration.timestamp).toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>
                            {iteration.isUserAction && (
                              <Chip 
                                label="User Action" 
                                color="info" 
                                size="small"
                                sx={{
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  height: { xs: 20, sm: 24 },
                                }}
                              />
                            )}
                          </Box>
                        
                        {iteration.userInstruction && (
                          <Alert 
                            severity="info" 
                            sx={{ 
                              mb: { xs: 1.5, sm: 2 },
                              py: { xs: 0.5, sm: 1 },
                            }}
                          >
                            <Typography 
                              variant="body2"
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                              <strong>User instruction:</strong> {iteration.userInstruction}
                            </Typography>
                          </Alert>
                        )}
                        
                        {/* Show previous prompt if available */}
                        {iteration.promptVersion && !iteration.isUserAction && (
                          <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                            <Typography 
                              variant="subtitle2" 
                              color="text.secondary" 
                              gutterBottom
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                              Previous Prompt:
                            </Typography>
                            <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50' }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap', 
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                  lineHeight: 1.5,
                                }}
                              >
                                {iteration.promptVersion}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        
                        {/* Show improved/current prompt */}
                        <Box>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                          }}>
                            <Typography 
                              variant="subtitle2" 
                              color="primary" 
                              gutterBottom
                              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                            >
                              {iteration.isUserAction ? 'Current Prompt:' : 'Improved Prompt:'}
                            </Typography>
                            <Tooltip title="Copy to clipboard">
                              <IconButton 
                                size="small" 
                                onClick={() => navigator.clipboard.writeText(iteration.prompt)}
                                sx={{ 
                                  mb: 0.5,
                                  minWidth: 44,
                                  minHeight: 44,
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'primary.50' }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                lineHeight: 1.5,
                              }}
                            >
                              {iteration.prompt}
                            </Typography>
                          </Paper>
                        </Box>

                        {/* Video Preview */}
                        {iteration.thumbnailUrl && (
                          <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                            <Card>
                              <CardMedia
                                component="img"
                                height={isMobile ? "200" : "300"}
                                image={iteration.thumbnailUrl}
                                alt="Video thumbnail"
                              />
                              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  justifyContent: 'space-between', 
                                  alignItems: { xs: 'flex-start', sm: 'center' },
                                  gap: { xs: 1, sm: 0 },
                                }}>
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                  >
                                    {iteration.duration}s • {iteration.aspectRatio} • {iteration.style}
                                  </Typography>
                                  {iteration.generatedVideoUrl && (
                                    <Button
                                      startIcon={!isMobile && <PlayArrowIcon />}
                                      variant="contained"
                                      size="small"
                                      href={iteration.generatedVideoUrl}
                                      target="_blank"
                                      fullWidth={isMobile}
                                    >
                                      Play Video
                                    </Button>
                                  )}
                                </Box>
                              </CardContent>
                            </Card>
                          </Box>
                        )}

                        {iteration.error && (
                          <Alert 
                            severity="error" 
                            sx={{ 
                              mt: { xs: 1.5, sm: 2 },
                              py: { xs: 0.5, sm: 1 },
                            }}
                          >
                            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {iteration.error}
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </TabPanel>
      )}
    </Container>
  );
}

export default VideoPromptIterator;